'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import DemoCredentials from './DemoCredentials';

interface LoginValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

const DEMO_EMAIL = 'arjun.sharma@student.edu';
const DEMO_PASSWORD = 'ExamReady@2026';

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
  } = useForm<LoginValues>({ defaultValues: { rememberMe: false } });

  const onSubmit = async (data: LoginValues) => {
    setIsLoading(true);
    // Backend integration point — POST /api/auth/login
    await new Promise((r) => setTimeout(r, 1200));

    if (data.email === DEMO_EMAIL && data.password === DEMO_PASSWORD) {
      toast.success('Welcome back, Arjun! 👋', { description: 'Redirecting to your dashboard...' });
      await new Promise((r) => setTimeout(r, 800));
      router.push('/exam-prep-dashboard');
    } else {
      setError('root', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      setIsLoading(false);
    }
  };

  const handleAutofill = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>
          Welcome back
        </h2>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Sign in to continue your exam prep
        </p>
      </div>

      {/* Google OAuth */}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border text-sm font-semibold transition-all duration-150 hover:bg-muted/40 active:scale-95"
        style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--card)' }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335" />
        </svg>
        Continue with Google
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
        <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>or</span>
        <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
      </div>

      {/* Root error */}
      {errors.root && (
        <div
          className="flex items-start gap-2.5 p-3.5 rounded-xl text-sm animate-slide-up"
          style={{ backgroundColor: 'var(--negative-bg)', border: '1px solid rgba(220,38,38,0.2)', color: 'var(--negative)' }}
        >
          <Icon name="ExclamationCircleIcon" size={16} className="shrink-0 mt-0.5" />
          {errors.root.message}
        </div>
      )}

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="login-email" className="block text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
          Email address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="arjun.sharma@student.edu"
          className="w-full px-4 py-3 rounded-xl border text-sm transition-all duration-150 outline-none focus:ring-2"
          style={{
            backgroundColor: 'var(--input)',
            borderColor: errors.email ? 'var(--negative)' : 'var(--border)',
            color: 'var(--foreground)',
          }}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p className="text-xs font-medium" style={{ color: 'var(--negative)' }}>{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label htmlFor="login-password" className="block text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
            Password
          </label>
          <button
            type="button"
            className="text-xs font-semibold hover:underline"
            style={{ color: 'var(--primary)' }}
          >
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            className="w-full px-4 py-3 pr-12 rounded-xl border text-sm transition-all duration-150 outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--input)',
              borderColor: errors.password ? 'var(--negative)' : 'var(--border)',
              color: 'var(--foreground)',
            }}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {errors.password && (
          <p className="text-xs font-medium" style={{ color: 'var(--negative)' }}>{errors.password.message}</p>
        )}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2.5">
        <input
          id="remember-me"
          type="checkbox"
          className="w-4 h-4 rounded accent-primary"
          {...register('rememberMe')}
        />
        <label htmlFor="remember-me" className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Keep me signed in for 30 days
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3.5 rounded-xl text-sm font-bold transition-all duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
      >
        {isLoading ? (
          <>
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Signing in...
          </>
        ) : (
          'Sign In to ExamReady'
        )}
      </button>

      {/* Switch to signup */}
      <p className="text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
        New to ExamReady?{' '}
        <button type="button" onClick={onSwitchToSignup} className="font-semibold hover:underline" style={{ color: 'var(--primary)' }}>
          Create a free account
        </button>
      </p>

      {/* Demo credentials */}
      <DemoCredentials onAutofill={handleAutofill} />
    </form>
  );
}